<!-- FocalPathApp.svelte -->

<script>
	// Stores and components
	import {
		Dilemma,
		pathNames,
		pathValues,
		Criteria,
		CriteriaCategorization,
		CriteriaPolarity,
	} from "./UserData.js";
	import {
		suggestedItemsMap,
		iconMapping,
		suggestedCriteria,
		CriteriaAndTipsExample,
	} from "./SuggestedCriteria.js";
	import { pathID } from "./AppState.js";
	import AddPathButton from "./AddPathButton.svelte";
	import CriteriaManager from "./CriteriaManager.svelte";
	import Slider from "./Slider.svelte";
	import TipsSwitch from "./TipsSwitch.svelte";
	import Tips from "./Tips.svelte";
	import Example from "./ExampleTips.svelte";
	import Navbar from "../Navbar.svelte";
	// import BarChart from "./BarChart.svelte"; // Obsoleto
	import ResultsChart from "./ResultsChart.svelte";
	// import Graph from "./Graph.svelte"; //  Obsoleto
	import RadarChart from "./RadarChart.svelte";
	import { maxIndex } from "d3";
	import { push } from "svelte-spa-router";
	import { onMount } from "svelte";
	import analytics from "../utils/analytics.js";
	import decisionAnalytics from "../utils/decisionAnalytics.js";
	import cookieManager from "../utils/cookieManager.js";

	// Globals
	// let pathNamesState = $state($pathNames); // initialise from the store
	// let pathValuesState = $state($pathValues); // initialise from the store
	let showTips = $state(true);
	let showExample = $state(false);
	let graphCategories = $state([]);
	let graphPaths = $state([]);
	// let email = "";
	let userItems = $state([]); // Tengo que inicializarlo en este componente para poder alimentarlo luego con el ejemplo

	// Icons mapping now imported from SuggestedCriteria.js

	// const colors = ["#CBBAED", "#BF1363", "#..."]; // Add more colors as needed
	const colors = [
		"rgba(255,99,132,0.8)",
		"rgba(54,162,235,0.8)",
		"rgba(255,206,86,0.8)",
	]; // ResultsChart colors

	// Function to capture and download the page as an image

	// Helper: convert external images to inline base64 URIs so html2canvas captures them
	async function inlineSvgImages(root) {
		// Handle SVG <image> elements
		const svgImages = Array.from(root.querySelectorAll('image'));
		// Handle regular <img> elements with .svg src
		const htmlImages = Array.from(root.querySelectorAll('img[src$=".svg"]'));
		
		const originals = [
			...svgImages.map(img => ({
				el: img,
				src: img.getAttribute('href'),
				attr: 'href'
			})),
			...htmlImages.map(img => ({
				el: img,
				src: img.getAttribute('src'),
				attr: 'src'
			}))
		].filter(item => item.src && !item.src.startsWith('data:'));

		await Promise.all(
			originals.map(async ({ el, src, attr }) => {
				try {
					// Handle relative paths properly
					const fullSrc = src.startsWith('/') 
						? window.location.origin + src 
						: new URL(src, window.location.href).href;
					const res = await fetch(fullSrc);
					const blob = await res.blob();
					const reader = new FileReader();
					const dataURL = await new Promise((resolve) => {
						reader.onloadend = () => resolve(reader.result);
						reader.readAsDataURL(blob);
					});
					el.setAttribute(attr, dataURL);
				} catch (e) {
					console.warn('Failed to inline image', src, e);
				}
			})
		);

		return () => {
			// restore originals after capture
			originals.forEach(({ el, src, attr }) => el.setAttribute(attr, src));
		};
	}

	const captureAndDownload = async () => {
		try {
            // Load dom-to-image library (better SVG/form element support)
            if (!window.domtoimage) {
                await new Promise((resolve, reject) => {
                    const script = document.createElement("script");
                    script.src = "https://cdn.jsdelivr.net/npm/dom-to-image@2.6.0/dist/dom-to-image.min.js";
                    script.onload = resolve;
                    script.onerror = reject;
                    document.head.appendChild(script);
                });
            }

            // Scroll to top for consistent capture
            const previousScrollY = window.scrollY;
            window.scrollTo(0, 0);
            await new Promise((r) => requestAnimationFrame(r));

            const target = document.body;

            // Inline SVG images before capture (dom-to-image still needs this for <image> elements)
            const restoreImages = await inlineSvgImages(target);

            // Temporarily remove PayPal elements from DOM to avoid CORS errors
            const paypalElements = [
                ...target.querySelectorAll('form[action*="paypal.com"]'),
                ...target.querySelectorAll('img[src*="paypal"]'),
                ...target.querySelectorAll('img[src*="paypalobjects.com"]')
            ];
            const removedElements = paypalElements.map(el => ({
                element: el,
                parent: el.parentNode,
                nextSibling: el.nextSibling
            }));
            
            // Remove from DOM completely
            paypalElements.forEach(el => el.remove());

            // Create high-quality PNG blob
            const blob = await window.domtoimage.toBlob(target, {
                quality: 1.0,
                bgcolor: '#ffffff',
                width: target.scrollWidth,
                height: target.scrollHeight,
                style: {
                    transform: 'scale(1)',
                    transformOrigin: 'top left'
                }
            });

            // Restore scroll position
            window.scrollTo(0, previousScrollY);

            // Restore original image sources and re-insert PayPal elements
            restoreImages && restoreImages();
            removedElements.forEach(({ element, parent, nextSibling }) => {
                if (parent) {
                    if (nextSibling) {
                        parent.insertBefore(element, nextSibling);
                    } else {
                        parent.appendChild(element);
                    }
                }
            });

            // Download the blob
            const link = document.createElement("a");
            link.download = "analysis-screenshot.png";
            link.href = URL.createObjectURL(blob);
            link.click();
            
            // Clean up blob URL
            setTimeout(() => URL.revokeObjectURL(link.href), 100);

        } catch (error) {
            console.error("Error capturing screenshot:", error);
            // Fallback to print
            window.print();
        }
    };

// Function to toggle polarity for a specific criterion
	const toggleCriterionPolarity = (index) => {
		CriteriaPolarity.update((polarities) => {
			const newPolarities = [...polarities];
			// Ensure array is long enough
			while (newPolarities.length <= index) {
				newPolarities.push("Higher is better");
			}
			// Toggle the polarity
			newPolarities[index] =
				newPolarities[index] === "Higher is better"
					? "Lower is better"
					: "Higher is better";
			return newPolarities;
		});
	};

	// Reactive definitions
	// 2. Keep store ←→ state in sync
	// $effect(() => pathNames.set(pathNamesState)); // whenever child mutates
	// $effect(() => {
	// 	// if something else mutates the store
	// 	pathNamesState = $pathNames;
	// });
	// $effect(() => pathValues.set(pathValuesState));
	// $effect(() => {
	// 	pathValuesState = $pathValues;
	// });

	let numPaths = $derived(Object.keys($pathNames).length);
	let numCriteria = $derived($Criteria.length);
	// I need to update pathValues with placeholders as pathNames changes so then the Sliders can bind to these placeholders
	$effect.pre(() => {
		// pathValues is declared based on pathNames and the values from the Sliders
		const pathKeys = Object.keys($pathNames);
		const pathValueKeys = Object.keys($pathValues);
		const currentCriteriaLength = $Criteria.length;

		// Check if there are any keys in pathValues that are not in pathNames
		const keysToRemove = pathValueKeys.filter(
			(key) => !pathKeys.includes(key),
		);
		if (keysToRemove.length > 0) {
			// Update the pathValues store by removing the extra keys
			pathValues.update((currentValues) => {
				for (const keyToRemove of keysToRemove) {
					delete currentValues[keyToRemove];
				}
				return currentValues;
			});
		}

		// Clean up pathValues when criteria are deleted
		// Check if any path has more values than current criteria count
		// BUT skip cleanup if we're in example mode or if criteria length is 0 (during transitions)
		const needsCriteriaCleanup = currentCriteriaLength > 0 && 
			!showExample && 
			pathKeys.some(pathKey => 
				$pathValues[pathKey] && $pathValues[pathKey].length > currentCriteriaLength
			);
		
		if (needsCriteriaCleanup) {
			pathValues.update((currentValues) => {
				const updatedValues = { ...currentValues };
				pathKeys.forEach((pathKey) => {
					if (updatedValues[pathKey] && updatedValues[pathKey].length > currentCriteriaLength) {
						// Trim the array to match current criteria length
						updatedValues[pathKey] = updatedValues[pathKey].slice(0, currentCriteriaLength);
						console.log(`Trimmed pathValues for ${pathKey} from ${currentValues[pathKey].length} to ${currentCriteriaLength} values`);
					}
				});
				return updatedValues;
			});
			
			// Clear backend results when criteria structure changes to prevent stale radar chart data
			if (backendResults) {
				backendResults = null;
				console.log("Cleared backend results due to criteria deletion");
			}
		}

		// Initialize pathValues[pathKey] with an array of default values (e.g., 50)
		pathKeys.forEach((pathKey) => {
			if (!$pathValues[pathKey]) {
				// Always initialize missing paths, even during example mode
				console.log("Initializing pathValues array");
				$pathValues[pathKey] = new Array(currentCriteriaLength).fill(50);
			} else if (!showExample && $pathValues[pathKey].length < currentCriteriaLength) {
				// Only extend arrays for new criteria when NOT in example mode
				// (to preserve example data integrity)
				const currentLength = $pathValues[pathKey].length;
				const newValues = new Array(currentCriteriaLength - currentLength).fill(50);
				pathValues.update((currentValues) => ({
					...currentValues,
					[pathKey]: [...currentValues[pathKey], ...newValues]
				}));
				console.log(`Extended pathValues for ${pathKey} from ${currentLength} to ${currentCriteriaLength} values`);
			}
		});
	});

	// Reactive statement to transform data for Graph
	$effect(() => {
		graphCategories = $Criteria.map((criterion, i) => {
			// If criterion is an object, use its properties, otherwise create a new object
			const criterionObj =
				typeof criterion === "object" ? criterion : { name: criterion };
			return {
				...criterionObj, // Spread all properties from the criterion object
				// Override or add any additional properties
				type: $CriteriaCategorization[i] || "Critical",
				polarity: $CriteriaPolarity[i] || "Higher is better",
			};
		});
		graphPaths = Object.keys($pathNames).map((pathKey, index) => {
			return {
				name: $pathNames[pathKey],
				values: $pathValues[pathKey].map((value, criterionIndex) => {
					// Apply polarity inversion for display in RadarChart
					const polarity =
						$CriteriaPolarity[criterionIndex] || "Higher is better";
					return polarity === "Lower is better" ? 100 - value : value;
				}),
				color: colors[index % colors.length], // Cycles through the colors
			};
		});
	});

	//Declarations and reactive statements for showing the example
	let originalPathValues = {};
	// let originalCriteria = [];
	// let originalCriteriaCategorization = [];
	let originalPathNames = {};
	let originalDilemma = "";
	let originalUserItems = [];
	let originalCriteriaPolarity = []; // Store original polarity settings
	let originalPathID = 0;

	$effect(() => {
		console.log("originalUserItems:", $state.snapshot(originalUserItems));
	});

	// Data clone helper function — falls back to JSON trick in old browsers
	const deepClone = (v) => JSON.parse(JSON.stringify(v));

	function handleExampleToggle(on_off) {
		showExample = on_off;
		console.log("Example toggle changed to:", $state.snapshot(showExample));
		if (showExample) {
			// Store current values
			originalDilemma = $Dilemma;
			originalPathID = $pathID;
			originalPathValues = deepClone($pathValues);
			originalPathNames = deepClone($pathNames);
			originalUserItems = deepClone(userItems); // Store current userItems
			originalCriteriaPolarity = deepClone($CriteriaPolarity); // Store current polarity settings

			// Set example data
			$Dilemma =
				"I just got offered a new position with way better conditions but in a city abroad. It's an exciting opportunity for my career, but moving would be difficult and would have a big negative impact on my close relationships. It's been a couple days and I am switching back and forth between accepting and rejecting, there has to be a better way";
			$pathNames = {
				"Path 0": "Reject the offer",
				"Path 1": "Take the offer and move",
				"Path 2":
					"Try to negotiate remote working conditions so I don't have to move",
			};

			// Build example items from CriteriaAndTipsExample
			userItems = CriteriaAndTipsExample.map((example) => {
				// For criteria-based examples, get the full criteria object
				const criteria = example.exampleId
					? suggestedCriteria.find((c) => c.id === example.exampleId)
					: null;

				return {
					id: example.id || example.exampleId,
					name: criteria?.name || example.name,
					category: example.category,
					polarity: criteria?.defaultPolarity || example.polarity,
					example: example.exampleInfo,
					iconPath: criteria?.iconPath,
				};
			})
				// Ensure we have all required fields and filter out any undefined items
				.filter(
					(item) =>
						item &&
						item.id &&
						item.name &&
						item.category &&
						item.polarity,
				);
			$pathValues = {
				"Path 0": [25, 50, 50, 30],
				"Path 1": [90, 10, 60, 80],
				"Path 2": [75, 40, 75, 65],
			};
			$pathID = Object.keys($pathNames).length;
		} else {
			// Restore original data
			$Dilemma = originalDilemma;
			$pathID = originalPathID;
			$pathValues = deepClone(originalPathValues);
			userItems = deepClone(originalUserItems); // Restore original userItems
			$pathNames = deepClone(originalPathNames);
			// Restore original polarity settings
			CriteriaPolarity.set(deepClone(originalCriteriaPolarity));

			// Clear previous results and graphs to avoid mismatches
			backendResults = null;
			graphCategories = [];
			graphPaths = [];
		}
	}

	// Functions

	let backendResults = $state(null); // Initialize a variable to store the response of the back end
	let isProcessing = $state(false); // Loading state for the analysis
	const handleSubmit = async (e) => {
		e?.preventDefault?.();

		// Start loading state
		isProcessing = true;

		// Validate array lengths before processing
		if ($CriteriaPolarity.length !== $Criteria.length) {
			console.warn(
				`Polarity array length (${$CriteriaPolarity.length}) doesn't match criteria length (${$Criteria.length}). Using default polarity for missing items.`,
			);
		}

		// Process polarity: invert values for "Lower is better" criteria
		const processedPathValues = {};
		for (const [pathKey, values] of Object.entries($pathValues)) {
			processedPathValues[pathKey] = values.map((value, index) => {
				const polarity = $CriteriaPolarity[index] || "Higher is better"; // Fallback to default
				// Invert the value if polarity is "Lower is better" (scale 0-100 becomes 100-0)
				return polarity === "Lower is better" ? 100 - value : value;
			});
		}

		// Convert criterion objects to just names for the backend
		const criteriaNames = $Criteria.map((criterion) =>
			typeof criterion === "object" ? criterion.name : criterion,
		);

		try {
			const response = await fetch("/process-data", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					dilema: $Dilemma,
					dilemma: $Dilemma, // Add both for compatibility
					pathNames: $pathNames,
					pathValues: processedPathValues, // Use processed values
					criteria: criteriaNames, // Send only the names
					criteriaCategorization: $CriteriaCategorization,
					// Analytics data
					sessionId: cookieManager.sessionId,
					userId: cookieManager.userId,
					decisionId: decisionAnalytics.currentDecision,
					sessionDuration: cookieManager.getSessionDuration(),
				}),
			});

			if (response.ok) {
				const results = await response.json();
				
				// Add random delay between 1.5-2.5 seconds before showing results
				const randomDelay = Math.random() * 1000 + 1500; // 1500ms to 2500ms
				setTimeout(() => {
					backendResults = results; // Set results after delay
					isProcessing = false; // Stop loading state

					// Track decision completion
					const completionData = {
						pathNames: $pathNames,
						criteria: criteriaNames,
						criteriaCategorization: $CriteriaCategorization,
						results: results,
						usedTips: showTips,
						usedExample: showExample
					};
					decisionAnalytics.trackDecisionCompleted(results, completionData);

					// Scroll to results after a brief moment
					setTimeout(() => {
						const resultsSection = document.getElementById("results-section");
						if (resultsSection) {
							resultsSection.scrollIntoView({
								behavior: "smooth",
								block: "start",
							});
						}
					}, 100);

					console.log("Results: ", $state.snapshot(results));
				}, randomDelay); // Random delay between 1.5-2.5 seconds
			} else {
				// Handle error case
				isProcessing = false;
				console.error("Error:", $state.snapshot(response.status));
			}
		} catch (error) {
			// Handle network errors
			isProcessing = false;
			console.error("Network error:", error);
		}
	};

	// const handleEmailSubmit = () => {
	// 	// Handle the form submission here
	// 	console.log("Submitted email:", email);
	// 	// You might want to send this data to a server or an email marketing tool
	// };

	// Function for making the buttons navigate to the other webpage
	function goToHome() {
		push("/");
	}

	// PayPal SDK donation button initialization
	let paypalScriptLoaded = false;
	let paypalInitialized = false;

	onMount(() => {
		// Initialize analytics tracking for FocalPath page
		analytics.trackPageView('focal_path', {
			timestamp: Date.now(),
			userAgent: navigator.userAgent,
			referrer: document.referrer
		});
		
		// Start decision analytics tracking
		decisionAnalytics.startDecision();
		
		loadPayPalSDK();
	});

	function loadPayPalSDK() {
		// Check if PayPal SDK is already loaded
		if (window.PayPal && window.PayPal.Donation) {
			initializePayPalButton();
			return;
		}

		// Avoid loading script multiple times
		if (paypalScriptLoaded) return;
		paypalScriptLoaded = true;

		const script = document.createElement('script');
		script.src = 'https://www.paypalobjects.com/donate/sdk/donate-sdk.js';
		script.charset = 'UTF-8';
		script.async = true;
		
		script.onload = () => {
			initializePayPalButton();
		};
		
		script.onerror = () => {
			console.warn('PayPal SDK failed to load, falling back to simple donation link');
			showFallbackDonation();
		};
		
		document.head.appendChild(script);
	}

	function initializePayPalButton() {
		if (paypalInitialized) return;
		
		// Wait for the donate button container to be available
		const checkContainer = () => {
			const container = document.getElementById('donate-button');
			if (container && window.PayPal && window.PayPal.Donation) {
				try {
					// Clear any existing content
					container.innerHTML = '';
					
					window.PayPal.Donation.Button({
						env: 'production',
						hosted_button_id: 'BKRSHLZQ2K4J2',
						image: {
							src: 'https://www.paypalobjects.com/en_US/i/btn/btn_donate_LG.gif',
							alt: 'Donate with PayPal button',
							title: 'PayPal - The safer, easier way to pay online!',
						}
					}).render('#donate-button');
					
					paypalInitialized = true;
				} catch (error) {
					console.warn('PayPal button initialization failed:', error);
					showFallbackDonation();
				}
			} else {
				// Retry after a short delay
				setTimeout(checkContainer, 100);
			}
		};
		
		checkContainer();
	}

	// Fallback donation method when PayPal SDK can't load or initialize
	function showFallbackDonation() {
		const donateButton = document.getElementById('donate-button');
		if (donateButton) {
			donateButton.innerHTML = `
				<a href="https://www.paypal.com/donate/?hosted_button_id=BKRSHLZQ2K4J2" 
				   target="_blank" 
				   style="display: inline-block; padding: 10px 20px; background-color: #0070ba; color: white; text-decoration: none; border-radius: 5px; font-weight: bold;">
					💝 Support Focal Path
				</a>
			`;
		}
	}


	// Flags for user tailoring Feedback in the results section
	let isExtraHelpNeeded = $state(false);
	let isNarrowFraming = $derived(numPaths < 3);
	let isDeepAnalysis = $derived(numCriteria + numPaths >= 7);

	// Logging
	$effect(() => {
		pathValues // Subscribe to the pathValues store
			.subscribe((value) =>
				console.log("pathValues: ", $state.snapshot(value)),
			);
	}); // Log the value to the console whenever it changes
	$effect(() => {
		pathNames // Subscribe to the pathNames store
			.subscribe((value) =>
				console.log("pathNames: ", $state.snapshot(value)),
			);
	}); // Log the names to the console whenever it changes
	$effect(() => {
		Criteria.subscribe((value) =>
			console.log("Criteria: ", $state.snapshot(value)),
		);
	}); // Subscribe to the Criteria store // Log the names to the console whenever it changes
	$effect(() => {
		CriteriaCategorization.subscribe(
			(value) =>
				console.log("CriteriaCategorization: ", $state.snapshot(value)),
			// console.log("Graph Categories", $state.snapshot(graphCategories))
		);
	}); // Subscribe to the Criteria store // Log the names to the console whenever it changes
	$effect(() => {
		CriteriaPolarity.subscribe((value) =>
			console.log("CriteriaPolarity: ", $state.snapshot(value)),
		);
	}); // Subscribe to the CriteriaPolarity store
</script>

<!-- Header Section -->
<section class="hero is-bold">
	<div class="hero-body">
		<div
			class="container has-text-centered"
			role="button"
			tabindex="0"
			onclick={goToHome}
			onkeydown={goToHome}
		>
			<img
				src="Nombre tipográfico.svg"
				alt="Lambda Icon"
				style="width: 70vw; max-width: 500px;"
			/>
			<!-- Subtitle -->
			<h2
				class="subtitle"
				style="padding: 1.5em; font-size: medium; color: lightgray;"
			>
				15 minutes of thinking, a lifetime of consequences
			</h2>
		</div>
	</div>

	<!-- <Navbar /> -->
</section>

<div class="appContent">
	{#if showExample}
		<div style="width:100vw; max-width:600px;">
			<Example>
				{#snippet children()}
					<p style="font-size: medium; text-align:center;">
						Sample decision
					</p>
				{/snippet}
			</Example>
		</div>
	{/if}

	<div
		style="display:flex; width: 90vw; align-items: center; max-width: 600px;"
	>
		<!-- <p>What's on your mind?</p> -->
		<img
			src="./Icons/Lambda icon decision box.svg"
			alt="Lambda Icon"
			style="height: 1.5em; margin-right:1.5em;"
		/>
		<textarea
			placeholder="Clarity begins with writing your dilemma — start here…"
			title="Try to be clear and concise"
			style="margin-top: 10px; width: 80%; font-size: large;"
			bind:value={$Dilemma}
		></textarea>
	</div>

	<div class="tips-container">
		<TipsSwitch
			bind:active={showTips}
			iconPath="/Icons/book-opened.svg"
			tooltip="Guide me"
			backgroundColor="#d6fffd"
			ontoggle={(val) => console.log("showTips toggled to", val)}
			id="tips-toggle"
		/>

		<TipsSwitch
			iconPath="/Icons/light-bulb-svgrepo-com.svg"
			tooltip="Show me an example"
			bind:active={showExample}
			backgroundColor="#fff4a9"
			ontoggle={handleExampleToggle}
			id="example-toggle"
		/>
	</div>

	{#if showTips}
		<Tips>
			{#snippet children()}
				<!-- declare the snippet -->
				<!-- 🔑 declare the snippet -->
				<p
					style="font-size: medium; text-align:center; margin-bottom:1em;"
				>
					Guide
				</p>
				<!-- 
				<p>Properly defining the issue is half the battle</p>
				<br /> -->
				<p>
					Try to be as <b>clear</b> and <b>concise</b> as you are
					able, but also dig deep and reflect about
					<b>why you want</b>
					or
					<b>need</b> to make this choice
				</p>

				<br />
				<p>The right toggle shows an example</p>
				<br>

				<p>When you are happy, click <b>Add Path</b></p>
				<!-- <br /> -->
				<!-- Properly defining the issue is half the battle -->
			{/snippet}
		</Tips>
	{/if}

	<!-- {#if $Dilemma.length > 10}
		{/if} -->

	{#if numPaths > 0}
		<p class="subtle-text" style="padding:2em;">
			What possible paths do you see?
		</p>
	{/if}

	<AddPathButton bind:pathNames={$pathNames} />
	<div class = "AddPathButton-scrolling-anchor" style = "margin-top:1em"></div>

	{#if showTips && numPaths > 0}
		<Tips>
			{#snippet children()}
				Write down the possible alternatives or <b>paths</b> you can see
				without judging them
			{/snippet}
		</Tips>
	{/if}

	{#if numPaths > 1}
		<div class="gap" style="margin-bottom: 100px;"></div>

		<!-- Incluyo la actualización de las stores en el propio componente -->
		{#if showExample}
			<div style="max-width: 600px;">
				<Example
					>{#snippet children()}From the list of calibrated questions,
						these seem the most relevant to me for this decision<br
						/><br /> I also created the factor: "Expected Stress"{/snippet}</Example
				>
			</div>
		{/if}
		<CriteriaManager {iconMapping} bind:userItems />
		{#if showTips}
			<Tips
				>{#snippet children()}Choose what feels most relevant for this
					decision only{/snippet}</Tips
			>
		{/if}
		{#if showTips && numCriteria > 0}
			<Tips
				>{#snippet children()}
					You can also click on the "Critical/Important" tag to
					prioritize
				{/snippet}</Tips
			>
		{/if}

		<div class="gap" style="margin-bottom: 50px;"></div>
		{#if numCriteria > 0}
			<p style="text-align: center; padding: 1.5em; margin-top:-1em;">
				Now <b>evaluate each path</b> according to one factor at a time
				and <b>relative to each other</b>
			</p>

			{#if showTips}
				<Tips
					>{#snippet children()}
						<p>
							We are trying to quantify very broad concepts. Play
							around with the sliders while reflecting on the
							question, until the <b><i>relationship</i></b>
							between the
							<i>values</i> feels right
						</p>
						<br />

						<!-- <p>A low score (to the left) means this path has a negative
					outcome for this factor, the opposite is true for high
					scores.</p> -->
						<!-- <br /><br />
					For example, a high score for Cost would mean the path
					<b>does not </b> cost much relative to the others. -->
					{/snippet}</Tips
				>
			{/if}
			{#each $Criteria as c, index}
				<div id="assessment-section">
					<div class="has-text-centered">
						<div
							class="criteria-container"
							style="width: 100%; max-width: 600px; margin-top: 1em;"
						>
							<!-- Icon for the criterion -->
							<img
								src={c.icon ||
									iconMapping[c.name || c] ||
									iconMapping["default"]}
								alt={c.name || c}
								class="criteria-icon"
								style="height: 4em; width: auto;"
							/>

							<!-- Criterion name -->
							<div style="padding: 1em"><i>{c.name || c}</i></div>
							<!-- Criterion slider -->
							<div style="width: 100%;">
								{#each Object.entries($pathNames) as [pathKey, Name]}
									<Slider
										bind:value={$pathValues[pathKey][index]}
									>
										<!-- Path name -->
										{#snippet children()}
											<li title={Name}>
												{Name.length > 30
													? `${Name.slice(0, 25)}…`
													: Name}
											</li>
										{/snippet}
									</Slider>
								{/each}
							</div>

							<!-- Polarity toggle for this criterion (hidden for suggested + default 'Higher is better') -->
							{#if !(suggestedItemsMap[c.name || c] === "Higher is better" && (c.polarity || $CriteriaPolarity[index] || "Higher is better") === "Higher is better")}
								<div
									style="text-align: center; margin-top: 1em; margin-bottom: 1em;"
								>
									<div
										class={($CriteriaPolarity[index] ||
											"Higher is better") ===
										"Higher is better"
											? "tag is-primary is-light"
											: "tag is-warning is-light"}
										role="button"
										tabindex="0"
										style="cursor: pointer; font-size: 0.9em;"
										onclick={() =>
											toggleCriterionPolarity(index)}
										onkeydown={(e) =>
											e.key === "Enter" &&
											toggleCriterionPolarity(index)}
									>
										{$CriteriaPolarity[index] ||
											"Higher is better"}
									</div>
								</div>
							{/if}

							{#if showExample}
								{#if userItems[index]?.example}
									<Example
										>{#snippet children()}{userItems[index]
												.example}{/snippet}</Example
									>
								{/if}
							{/if}
						</div>
					</div>
				</div>
			{/each}

			{#if showTips}
				<Tips
					>{#snippet children()}
						Don't be afraid to go back and forth tweaking and
						improving your analysis. We can do no better than the
						best path we consider <br /><br />
						Once you are satisfied, hit Finish to see the results
					{/snippet}</Tips
				>
			{/if}

			<div style="padding: 50px">
				{#if isProcessing}
					<!-- Loading Animation in place of button -->
					<div class="button-loading-container">
						<div class="spinning-icon-container">
							<img
								src="./Icons/Lambda icon decision box.svg"
								alt="Processing"
								class="spinning-lambda-icon"
							/>
						</div>
						<div class="weighing-text">Weighing factors...</div>
					</div>
				{:else}
					<form class="form-container" onsubmit={handleSubmit}>
						<button type="submit"> Finish </button>
					</form>
				{/if}
			</div>
		{/if}
	{/if}

	<!-- Fade Overlay -->
	{#if isProcessing}
		<div class="fade-overlay"></div>
	{/if}

	{#if backendResults}
		<div id="results-section">
			<h1 style="margin-top: 4em; margin-bottom: 2em;">
				Your Focal Path is...
			</h1>
			<!-- <div style = "width: 600px;" > -->
			<ResultsChart
				data={Object.values(backendResults.results)}
				labels={Object.values($pathNames)}
			/>
			{#if backendResults && graphCategories.length > 2}
				<h1 style="margin: 2em;">because</h1>
				<RadarChart
					categories={graphCategories}
					paths={graphPaths}
					{iconMapping}
				/>
			{/if}

			<div class="block" style="text-align: center;">
				<p>If these results make sense to you, commit and act</p>
				<div style="display: flex; justify-content: flex-end; width: 100%; max-width: 600px; margin-top:3em; padding-right: 1em;">
				<button 
					onclick={captureAndDownload}
					class="save-button"
					title="Save Analysis"
					aria-label="Save Analysis"
				>
					<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
						<polyline points="17 21 17 13 7 13 7 21"></polyline>
						<polyline points="7 3v5h8"></polyline>
					</svg>
				</button>
			</div>
			<!-- <p> Otherwise, keep refining the analysis</p> -->

				<!-- Quito estas partes tras el cambio en las gráficas de resultados -->
				<!-- <div>
					<p style="padding: 3em">
						Your Focal Path is <br /><br />
						⭐
						<b style="font-size: x-large;"
							>{Object.values($pathNames)[
								maxIndex(Object.values(backendResults.results))
							]}</b
						> ⭐
					</p>
				</div> -->
				<!-- <div style="padding: 1.5em">
					<p style="margin-bottom: 1em; font-size: medium">
						Until you take action on your Focal Path,
						this is your <b>AVOID AT ALL COSTS distraction</b> list:
					</p>

					<ul>
						{#each Object.entries($pathNames) as [key, name], index}
							{#if index !== maxIndex(Object.values(backendResults.results))}
								<li style="padding: 0.3em">⚠️ {name}</li>
							{/if}
						{/each}
					</ul>
				</div> -->

				<!-- <p style="font-size: small; padding: 1.5em; margin-top: 3em;">
					Tip: Save your analysis so you can come back to it if you
					ever start doubting in the future
				</p> -->
			</div>

			<!-- {#if isNarrowFraming}
			<div>
					<p>
						⚠️ Warning: Narrow Framing detected. Consider exploring
						more paths.
					</p>
				</div>
			{/if} -->
		</div>

		{#if isExtraHelpNeeded}
			<div
				class="notification is-info"
				style="text-align:center; font-size: medium; margin-top: 2em;"
			>
				<p><b>1- Attain some distance</b></p>
				<!-- <hr /> -->
				<p class="block" style="padding: 2em;">
					Save your analysis and repeat it another day. If the results
					are very different, you may be subject to the first Villain
					in decision making: <br />
				</p>
				<img
					src="./Short term emotions.svg"
					alt="Short term emotions Icon"
					style="height: 5em; "
				/>
				<p style="padding: 1em">Short term emotion</p>
				<hr />
				<p><b>2- Avoid "Resulting"</b></p>
				<p style="padding: 2em;">
					If you had to choose between a six-sided die and a ten-sided
					die for maximizing a die roll, you could choose the
					six-sided and still roll higher than the ten-sided. That
					does not mean the decision was correct. Judging a decision
					only by it's results is known as "Resulting"
				</p>
				<div class="block columns">
					<div class="column">
						<img
							src="./Learn cycle simple.svg"
							alt="Learning loop Icon"
							style=" max-width: 80vw;"
						/>
					</div>
					<div class="column">
						<p style="padding: 2em;">
							As we commit to a path and monitor progress, it is
							critical to identify which consequences were due to
							chance and which were not. Only if this distinction
							is possible should we consider learning from the
							experience for future decisions
						</p>
					</div>
				</div>
				<hr />
				<div class="block">
					<p><b>3- Price for inaction</b></p>
					<p style="padding: 2em;">
						Ask yourself: What happens if I don't decide? <br />
						Look for ways to experiment on a small scale: Rent a car
						for a month if you are deciding wether to buy one
					</p>
				</div>
			</div>

			<!-- Survey/Encuesta -->
			<div
				class="content"
				style="text-align: center; padding: 2em; font-size: small; font-style: italic;"
			>
				<p>
					<!-- This simple app can only go so far in helping you make
								the best decisions. Help us shape an extended version
								with  -->
					This is an early access version of Focal Path. If you want to
					learn more about the other three villains of decision making,
					help us with
					<a
						style="font-size:medium;"
						href="https://forms.gle/PD7tAohbUq83FK4k9"
						target="_blank"
						rel="noopener noreferrer"><b>your feedback</b></a
					>
				</p>
				<p>
					Early contributors will get access to the pro version for
					free
				</p>

				<div id="donate-button-container">
					<div id="donate-button"></div>
				</div>
				<p><a href="https://discord.gg/rpbSVsQv">Discord</a></p>
			</div>
		{/if}

		<footer class="footer" >
			<div class="container" style="font-size: small;">
				<!-- Quote -->
				<div class="content has-text-right">
					<!-- <blockquote> -->
					<em>Action expresses priorities</em>
					<p class="author">- M. Gandhi</p>
					<!-- </blockquote> -->
				</div>

				<div class="content has-text-right">
					<!-- <blockquote> -->
					<em
						>The successful warrior is the average man with
						laser-like focus</em
					>
					<p class="author">- Bruce Lee</p>
					<!-- </blockquote> -->
				</div>

				<div class="content has-text-right">
					<!-- <blockquote> -->
					<em
						>You are not your body and hair-style, but your capacity
						for choosing well. If your choices are beautiful, so too
						will you be</em
					>
					<p class="author">- Epictetus, I AC</p>
					<!-- </blockquote> -->
				</div>
				<!-- En quotes -->

				<div class="extra-help-checkbox">
					<label class="checkbox">
						<input
							class="checkbox"
							type="checkbox"
							bind:checked={isExtraHelpNeeded}
							style="transform: scale(1.5);"
						/>
						Tell me more and help me refine my decision
					</label>
					<p>
						<a href="https://forms.gle/B9Rxpow3CZ4Tu4vd8"
							>Report a bug</a
						>
					</p>
				</div>
			</div>
		</footer>
	{/if}
</div>

<style>
	/* Fade Overlay */
	.fade-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background: rgba(0, 0, 0, 0.3);
		z-index: 999;
		pointer-events: none;
		animation: fade-in 0.3s ease-out;
	}

	/* Button Loading Animation Styles */
	.button-loading-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 20px 32px;
		background: white;
		border-radius: 12px;
		position: relative;
		z-index: 1000;
	}

	.spinning-icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.spinning-lambda-icon {
		width: 32px;
		height: 32px;
		animation: spin 1.5s linear infinite;
	}

	.weighing-text {
		font-size: 0.9rem;
		color: #4a5568;
		font-weight: 500;
		font-style: italic;
		text-align: center;
	}

	/* Animations */
	@keyframes fade-in {
		0% { opacity: 0; }
		100% { opacity: 1; }
	}

	@keyframes spin {
		0% { transform: rotate(0deg); }
		100% { transform: rotate(360deg); }
	}

	.save-button {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: white;
		border: 1px solid #dbdbdb;
		border-radius: 4px;
		padding: 10px 15px;
		cursor: pointer;
		transition: all 0.2s ease;
		margin: 10px 0;
	}

	.save-button:hover {
		border-color: #b5b5b5;
		color: #363636;
	}

	.save-button:active {
		transform: scale(0.98);
	}

	.save-button svg {
		width: 20px;
		height: 20px;
		color: #4a4a4a;
	}

	.tips-container {
		display: flex;
		justify-content: center;
		gap: 0.5rem;
		margin: 1rem 0;
		width: 100%;
		max-width: 600px;
	}

	.appContent {
		display: flex;
		flex-direction: column;
		align-items: center;
		font-size: large;
	}

	textarea {
		height: 200px;
		/* margin-bottom: 20px; */
		width: 90%; /* Takes up 90% of the viewport width */
		padding: 10px;
		/* border: 1px solid #ccc; */
		border-radius: 10px;
		box-sizing: border-box;
		transition: border 0.3s ease;
		max-width: 600px; /* Max width to keep readability in check on larger devices */
		box-sizing: border-box;
		resize: vertical; /* Allow vertical resizing only */
	}

	/* Assessment section styling for consistent width */
	#assessment-section {
		width: 100%;
		max-width: 600px;
		margin: 0 auto;
		padding: 1rem;
		box-sizing: border-box;
	}

	/* Ensure criteria container has consistent width */
	.criteria-container {
		width: 100% !important;
		max-width: 600px !important;
		margin: 1em auto !important;
		box-sizing: border-box;
	}

	#results-section {
		display: flex;
		justify-content: center;
		align-items: center;
		flex-direction: column;
	}

	.extra-help-checkbox {
		text-align: center;
		font-size: medium;
		margin: 1rem; /* Adjust as needed */
		padding: 1em;
		/* Other styles as needed */
	}

	.author {
		font-size: smaller;
		padding: 0.5em;
	}
</style>
